import type { ProjectGraphNode, ProjectGraphResponse } from '@avisoma/shared';
import { indexOf, SERIAL_KEY_FIELD } from './graphIndex';
import { nodeLabel } from './nodeLabel';

const MAX_RESULTS = 20;

/** How a result matched: a whole field, the start of one, or somewhere inside one. Best first. */
export type MatchReason = 'exact' | 'prefix' | 'contains';

export type SearchResult = { node: ProjectGraphNode; reason: MatchReason };

const REASON_RANK: Record<MatchReason, number> = { exact: 0, prefix: 1, contains: 2 };
/** Within a reason, results run in this kind order; a kind added after this release comes last. */
const KIND_RANK: Record<string, number> = { engine: 0, aircraft: 1, document: 2, part: 3, reference: 4 };
const kindRank = (kind: string) => KIND_RANK[kind] ?? Object.keys(KIND_RANK).length;
const compare = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** The best way `text` holds `needle`, or `null` when it does not. */
function reasonOf(text: string, needle: string): MatchReason | null {
  if (text === needle) return 'exact';
  if (text.startsWith(needle)) return 'prefix';
  return text.includes(needle) ? 'contains' : null;
}

type Ranked = SearchResult & { label: string };

const byRank = (a: Ranked, b: Ranked) =>
  REASON_RANK[a.reason] - REASON_RANK[b.reason] ||
  kindRank(a.node.kind) - kindRank(b.node.kind) ||
  compare(a.label, b.label) ||
  compare(a.node.id, b.node.id);

/**
 * Case-insensitive search over the identifying fields and label of each node, and an exact match on its id. Exact matches come first,
 * then prefix matches, then the rest; within each, by kind (engine, aircraft, document, part, reference), then
 * label. The best 20.
 */
export function searchGraph(graph: ProjectGraphResponse, query: string): SearchResult[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];
  const serialNeedle = needle.replace(/\s+/g, '');
  const { nodesById, searchFields } = indexOf(graph);
  const hits: Ranked[] = [];
  for (const [id, fields] of searchFields) {
    let best: MatchReason | null = null;
    for (const { field, text } of fields) {
      const target = field === SERIAL_KEY_FIELD ? serialNeedle : needle;
      // An id counts only whole: part of a `doc:<uuid>` id would match any short hex or digit query.
      const reason = field === 'id' ? (text === needle ? 'exact' : null) : reasonOf(text, target);
      if (reason !== null && (best === null || REASON_RANK[reason] < REASON_RANK[best])) best = reason;
      if (best === 'exact') break;
    }
    const node = nodesById.get(id);
    if (best && node) hits.push({ node, reason: best, label: nodeLabel(node).toLowerCase() });
  }
  return hits
    .sort(byRank)
    .slice(0, MAX_RESULTS)
    .map(({ node, reason }) => ({ node, reason }));
}
